"use client";

// Compteur de CLÉS Reps dans la capsule du haut, à côté de la série et des
// fragments : une monnaie comme une autre. ∞ pour les abonnés / l'onboarding,
// sinon le nombre de clés restantes du jour (rafraîchi au focus de la fenêtre).
// Le popup utilise le gabarit commun ResourcePopup. CTA dégradé (upsell) UNIQUEMENT
// pour les non-abonnés ; un abonné en illimité n'a rien à débloquer -> pas de CTA.
import Image from "next/image";
import ResourcePopup from "../ResourcePopup/ResourcePopup";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { hasUnlimitedEnergy } from "@/lib/reps/access";

function formatCountdown(targetIso) {
    if (!targetIso) return "";
    const ms = new Date(targetIso).getTime() - Date.now();
    if (ms <= 0) return "0s";
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}h ${String(m).padStart(2, "0")}m ${String(sec).padStart(2, "0")}s`;
}

export default function Keys({
    session,
    showTipsOnTop,
    withoutPopup = false,
    hoverRounded = "rounded-xl",
}) {
    const t = useTranslations("layout");
    const sessionUnlimited = hasUnlimitedEnergy(session && session.user);
    const [state, setState] = useState(null);
    const [openInfos, setOpenInfos] = useState(false);
    const [, setTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        function load() {
            fetch("/api/reps/keys-state", { method: "POST" })
                .then((r) => (r.ok ? r.json() : null))
                .then((d) => {
                    if (!cancelled && d && d.keys) setState(d.keys);
                })
                .catch(() => {});
        }
        load();
        const onRefresh = () => load();
        window.addEventListener("focus", onRefresh);
        window.addEventListener("reps:keys-changed", onRefresh);
        return () => {
            cancelled = true;
            window.removeEventListener("focus", onRefresh);
            window.removeEventListener("reps:keys-changed", onRefresh);
        };
    }, []);

    // Décompte de recharge : intervalle seulement quand le popup est ouvert.
    useEffect(() => {
        if (!openInfos) return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [openInfos]);

    const unlimited = state ? state.unlimited : sessionUnlimited;
    const count = state && !state.unlimited ? state.keys ?? 0 : null;
    const starterActive = !!(
        state && state.starterKeys > 0 && state.starterExpiresAt
    );

    return (
        <div
            className={`flex items-center gap-1.5 hover:background-blur dark:hover:bg-white/10 duration-100 cursor-pointer p-2 ${hoverRounded} relative`}
            onMouseOver={() => setOpenInfos(true)}
            onMouseOut={() => setOpenInfos(false)}
        >
            <Image src="/static/icons/key.svg" width={15} height={17} alt="" />
            <div className="text-sm text-gray-800/70 dark:text-lightBlue font-bold font-geistMono tabular-nums">
                {/* Illimité : façon Duolingo, un infini dessiné comme une
                    icône (épais) dans la couleur de la ressource, jamais le
                    glyphe texte ∞ (trop petit et gris = quantité banale) */}
                {unlimited ? (
                    <>
                        <SolarIcon
                            name="infinity-bold"
                            className="w-[18px] h-[18px] text-amber-500 dark:text-amber-400"
                        />
                        <span className="sr-only">
                            {t("Keys_unlimited_title")}
                        </span>
                    </>
                ) : count !== null ? (
                    count
                ) : (
                    "·"
                )}
            </div>

            <ResourcePopup
                open={openInfos && !withoutPopup}
                showTipsOnTop={showTipsOnTop}
                icon={
                    <Image
                        src="/static/icons/key.svg"
                        width={17}
                        height={19}
                        alt=""
                    />
                }
                title={t("Keys_title")}
                badge={
                    !unlimited && state && state.eventActive ? (
                        <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 dark:bg-amber-500/10">
                            <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
                                {t("Keys_event")}
                            </span>
                        </div>
                    ) : null
                }
                cta={
                    unlimited
                        ? null
                        : {
                              href: "/pricing",
                              label: t("Keys_unlock_all"),
                              variant: "upsell",
                          }
                }
            >
                <div className="text-[#06050F]/60 dark:text-lightBlue font-geistSans text-sm mt-1">
                    {unlimited
                        ? t("Keys_unlimited_title")
                        : starterActive
                          ? t("Keys_starter_lead", { count: state.starterKeys })
                          : t("Keys_left_today", { count: count ?? 0 })}
                </div>
                {!starterActive && (
                    <div className="text-[#06050F]/60 dark:text-lightBlue font-geistSans text-sm">
                        {unlimited
                            ? t("Keys_unlimited_sub")
                            : t("Keys_use_to_unlock")}
                    </div>
                )}
                {starterActive ? (
                    <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-amber-600 dark:text-amber-400 tabular-nums">
                        <Image src="/static/icons/clock.svg" width={12} height={12} alt="" />
                        {t("Keys_starter_deadline", {
                            time: formatCountdown(state.starterExpiresAt),
                        })}
                    </div>
                ) : !unlimited && state && state.nextResetAt ? (
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-400 tabular-nums">
                        <Image src="/static/icons/clock.svg" width={12} height={12} alt="" />
                        {t("Keys_reset_in", {
                            time: formatCountdown(state.nextResetAt),
                        })}
                    </div>
                ) : null}
            </ResourcePopup>
        </div>
    );
}
